import type { TierKey } from "@reach/core";
import { merchantFetch } from "../api";
import type { KbItem } from "../types";
import type {
  SettingsBillingGroup,
  SettingsFormGroup,
  SettingsHolidayGroup,
  SettingsKbGroup,
  SettingsModalsGroup,
  SettingsTrainingGroup,
} from "./useSettingsPageState";

type UseSettingsModalHandlersParams = {
  form: Pick<SettingsFormGroup, "loading" | "setLoading">;
  kb: Pick<SettingsKbGroup, "kbItems" | "pendingDeleteId" | "setPendingDeleteId" | "selectedKbId" | "setSelectedKbId" | "setKbDeleteLoading">;
  training: SettingsTrainingGroup;
  billing: Pick<SettingsBillingGroup, "pendingProviderTier" | "setPendingProviderTier" | "selectedProvider" | "promoCode" | "setPromoCode" | "showPromoField" | "setShowPromoField">;
  modals: Pick<SettingsModalsGroup, "setShowDeleteTopicModal" | "setShowHolidayModal" | "setShowProviderModal">;
  holiday: SettingsHolidayGroup;
  closeTrainingModal: () => void;
  doWordPressCheckout: (planId: TierKey, provider: "stripe" | "lemonsqueezy") => Promise<void>;
  fetchKb: () => void | Promise<void>;
  handleChange: (field: string, value: unknown) => void;
  shopDomain: string;
  showToast: (msg: string) => void;
};

export function useSettingsModalHandlers({
  form,
  kb,
  training,
  billing,
  modals,
  holiday,
  closeTrainingModal,
  doWordPressCheckout,
  fetchKb,
  handleChange,
  shopDomain,
  showToast,
}: UseSettingsModalHandlersParams) {
  const resetProviderModal = () => {
    modals.setShowProviderModal(false);
    billing.setPendingProviderTier(null);
    billing.setPromoCode("");
    billing.setShowPromoField(false);
  };

  const resetHolidayModal = () => {
    modals.setShowHolidayModal(false);
    holiday.setHName("");
    holiday.setEditingHolidayId(null);
    holiday.setSelectedDates({ start: new Date(), end: new Date() });
  };

  const handleHolidayAddOrUpdate = () => {
    const startStr = holiday.selectedDates.start.toLocaleDateString("en-CA");
    const endStr = holiday.selectedDates.end.toLocaleDateString("en-CA");
    const newEntry = {
      id: holiday.editingHolidayId || Math.random().toString(36).slice(2, 11),
      name: holiday.hName,
      startDate: startStr,
      endDate: endStr,
    };

    if (holiday.editingHolidayId) {
      holiday.setLocalHolidays((prev) =>
        prev.map((h) => (typeof h !== "string" && h.id === holiday.editingHolidayId ? newEntry : h))
      );
      holiday.setEditingHolidayId(null);
    } else {
      holiday.setLocalHolidays((prev) => [...prev, newEntry]);
    }

    holiday.setHName("");
    holiday.setSelectedDates({ start: new Date(), end: new Date() });
    holiday.setMonth({ month: new Date().getMonth(), year: new Date().getFullYear() });
  };

  const handleHolidaySave = () => {
    handleChange("holidays", holiday.localHolidays);
    resetHolidayModal();
  };

  const handleDeleteTopicConfirm = async () => {
    if (!kb.pendingDeleteId) return;
    kb.setKbDeleteLoading(true);
    const res = await merchantFetch(`/api/merchant/knowledge-base/${kb.pendingDeleteId}?domain=${shopDomain}`, { method: "DELETE" });
    kb.setKbDeleteLoading(false);

    if (res.ok) {
      showToast("Topic deleted.");
      if (kb.selectedKbId === kb.pendingDeleteId) {
        kb.setSelectedKbId(null);
        training.setQQuestion("");
        training.setQCustomReply("");
        training.setQSuggestions("");
        training.setQKeywords("");
        training.setQSource("manual");
        training.setQTargetUrl("");
        training.setQContent("");
        training.setDuplicateTopic(null);
      }
      fetchKb();
    } else {
      showToast("Failed to delete topic.");
    }

    kb.setPendingDeleteId(null);
    modals.setShowDeleteTopicModal(false);
  };

  const handleTrainingSave = async () => {
    if (!training.qQuestion || (!training.qCustomReply && !training.qContent)) {
      showToast("Question and either Auto Reply or Reference Content are required.");
      return;
    }

    training.setDuplicateTopic(null);

    const res = await merchantFetch(`/api/merchant/knowledge-base`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        domain: shopDomain,
        id: kb.selectedKbId || undefined,
        question: training.qQuestion,
        customReply: training.qCustomReply,
        suggestions: training.qSuggestions,
        keywords: training.qKeywords || undefined,
        source: training.qSource || "manual",
        targetUrl: training.qTargetUrl || undefined,
        content: training.qContent,
      }),
    });

    if (res.ok) {
      showToast(kb.selectedKbId ? "Topic updated!" : "Chatbot trained!");
      closeTrainingModal();
      fetchKb();
      return;
    }

    const data = await res.json().catch(() => ({}));
    if (res.status === 409 && data?.existingTopic) {
      training.setDuplicateTopic(data.existingTopic);
      return;
    }

    showToast(data?.error || "Failed to save topic.");
  };

  const handleProviderContinue = async () => {
    if (!billing.selectedProvider || !billing.pendingProviderTier) return;
    form.setLoading(true);
    modals.setShowProviderModal(false);
    await doWordPressCheckout(billing.pendingProviderTier, billing.selectedProvider);
    billing.setPendingProviderTier(null);
  };

  const handleEditKbItem = (item: KbItem) => {
    kb.setSelectedKbId(item.id);
    training.setQQuestion(item.title || item.question || "");
    training.setQCustomReply(item.reply || item.customReply || "");
    training.setQSuggestions(item.suggestions || "");
    training.setQKeywords(item.keywords || "");
    training.setQSource(item.source || "manual");
    training.setQTargetUrl(item.targetUrl || (item.sourceUrl && /^https?:\/\//i.test(item.sourceUrl) ? item.sourceUrl : ""));
    training.setQContent(item.content || "");
    training.setDuplicateTopic(null);
    training.setIsTrainingReadOnly(false);
    training.setIsTrainingModalOpen(true);
  };

  const handleViewKbItem = (item: KbItem) => {
    kb.setSelectedKbId(item.id);
    training.setQQuestion(item.title || item.question || "");
    training.setQCustomReply(item.reply || item.customReply || "");
    training.setQSuggestions(item.suggestions || "");
    training.setQKeywords(item.keywords || "");
    training.setQSource(item.source || "manual");
    training.setQTargetUrl(item.targetUrl || (item.sourceUrl && /^https?:\/\//i.test(item.sourceUrl) ? item.sourceUrl : ""));
    training.setQContent(item.content || "");
    training.setDuplicateTopic(null);
    training.setIsTrainingReadOnly(true);
    training.setIsTrainingModalOpen(true);
  };

  const handleOpenDuplicateTopic = () => {
    if (!training.duplicateTopic) return;
    const existing = kb.kbItems.find((item) => item.id === training.duplicateTopic?.id) || training.duplicateTopic;
    handleViewKbItem(existing);
  };

  return {
    handleDeleteTopicConfirm,
    handleEditKbItem,
    handleHolidayAddOrUpdate,
    handleHolidaySave,
    handleOpenDuplicateTopic,
    handleProviderContinue,
    handleTrainingSave,
    handleViewKbItem,
    resetHolidayModal,
    resetProviderModal,
  };
}
